import React, { useCallback, useRef, useMemo, useEffect, useState } from 'react';
import { useAnimationStore } from '../../store/animationStore';
import { cn } from '../../utils/cn';

interface TimelineRulerProps {
  className?: string;
  width?: number;
}

export const TimelineRuler: React.FC<TimelineRulerProps> = ({ className, width = 800 }) => {
  const {
    selectedAnimationId,
    animations,
    playback,
    viewport,
    setCurrentTime,
  } = useAnimationStore();

  const rulerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const animation = selectedAnimationId ? animations[selectedAnimationId] : null;
  const duration = animation?.duration || 1;
  const { pixelsPerSecond, scrollLeft } = viewport;
  const { fps, currentTime } = playback;

  // Calculate tick marks based on zoom level
  const tickConfig = useMemo(() => {
    // Determine major tick interval based on pixels per second
    let majorInterval: number;
    let minorDivisions: number;
    let showFrames: boolean;

    if (pixelsPerSecond >= 200) {
      // High zoom: show every 0.1s with frame subdivisions
      majorInterval = 0.1;
      minorDivisions = Math.min(10, Math.floor(fps / 10));
      showFrames = true;
    } else if (pixelsPerSecond >= 100) {
      // Medium zoom: show every 0.5s
      majorInterval = 0.5;
      minorDivisions = 5;
      showFrames = false;
    } else if (pixelsPerSecond >= 50) {
      // Low zoom: show every 1s
      majorInterval = 1;
      minorDivisions = 4;
      showFrames = false;
    } else {
      // Very low zoom: show every 5s
      majorInterval = 5;
      minorDivisions = 5;
      showFrames = false;
    }

    return { majorInterval, minorDivisions, showFrames };
  }, [pixelsPerSecond, fps]);

  // Generate tick marks
  const ticks = useMemo(() => {
    const { majorInterval, minorDivisions } = tickConfig;
    const minorInterval = majorInterval / minorDivisions;
    const result: { time: number; major: boolean; label?: string }[] = [];

    // Calculate visible time range
    const startTime = Math.max(0, scrollLeft / pixelsPerSecond);
    const visibleDuration = width / pixelsPerSecond;
    const endTime = Math.min(duration, startTime + visibleDuration + majorInterval);

    // Start from the first major tick before the visible area
    const firstMajor = Math.floor(startTime / majorInterval) * majorInterval;

    for (let time = firstMajor; time <= endTime; time += minorInterval) {
      const isMajor = Math.abs(time % majorInterval) < 0.0001 ||
                      Math.abs((time % majorInterval) - majorInterval) < 0.0001;

      let label: string | undefined;
      if (isMajor) {
        const seconds = Math.round(time * 10) / 10;
        if (seconds >= 60) {
          const mins = Math.floor(seconds / 60);
          const secs = Math.round(seconds % 60);
          label = `${mins}:${secs.toString().padStart(2, '0')}`;
        } else if (pixelsPerSecond >= 200) {
          // Show milliseconds at high zoom
          label = `${seconds.toFixed(1)}s`;
        } else {
          label = `${seconds}s`;
        }
      }

      result.push({ time, major: isMajor, label });
    }

    return result;
  }, [duration, pixelsPerSecond, scrollLeft, width, tickConfig]);

  // Convert time to x position
  const timeToX = useCallback(
    (time: number) => {
      return time * pixelsPerSecond - scrollLeft;
    },
    [pixelsPerSecond, scrollLeft]
  );

  // Convert x position to time
  const xToTime = useCallback(
    (x: number) => {
      const time = (x + scrollLeft) / pixelsPerSecond;
      return Math.max(0, Math.min(time, duration));
    },
    [pixelsPerSecond, scrollLeft, duration]
  );

  // Handle click to seek
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!rulerRef.current) return;

      const rect = rulerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const time = xToTime(x);

      setCurrentTime(time);
      setIsDragging(true);
    },
    [xToTime, setCurrentTime]
  );

  // Handle drag to scrub
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!rulerRef.current) return;

      const rect = rulerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const time = xToTime(x);

      setCurrentTime(time);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, xToTime, setCurrentTime]);

  // Playhead position
  const playheadX = timeToX(currentTime);

  // Format current time for display
  const formatTime = (time: number): string => {
    const frame = Math.round(time * fps);
    const totalFrames = Math.round(duration * fps);
    return `${time.toFixed(2)}s (${frame}/${totalFrames})`;
  };

  return (
    <div
      ref={rulerRef}
      className={cn(
        'relative h-6 bg-zinc-800 border-b border-zinc-700 select-none cursor-pointer overflow-hidden',
        className
      )}
      onMouseDown={handleMouseDown}
    >
      {/* Tick marks */}
      <svg className="absolute inset-0 w-full h-full" style={{ width }}>
        {ticks.map((tick, index) => {
          const x = timeToX(tick.time);
          if (x < -10 || x > width + 10) return null;

          return (
            <g key={index}>
              {/* Tick line */}
              <line
                x1={x}
                y1={tick.major ? 16 : 20}
                x2={x}
                y2={24}
                stroke={tick.major ? '#71717a' : '#52525b'}
                strokeWidth={tick.major ? 1 : 0.5}
              />
              {/* Label */}
              {tick.label && (
                <text
                  x={x + 3}
                  y={12}
                  fontSize={10}
                  fill="#a1a1aa"
                  fontFamily="system-ui, sans-serif"
                >
                  {tick.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Playhead */}
      {playheadX >= 0 && playheadX <= width && (
        <div
          className="absolute top-0 bottom-0 w-px bg-blue-500 pointer-events-none"
          style={{ left: playheadX }}
        >
          {/* Playhead handle */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3 h-3 bg-blue-500 rounded-sm" />
        </div>
      )}

      {/* Current time tooltip on drag */}
      {isDragging && playheadX >= 0 && playheadX <= width && (
        <div
          className="absolute top-full mt-1 px-2 py-0.5 bg-zinc-700 text-zinc-200 text-xs rounded whitespace-nowrap z-10 pointer-events-none"
          style={{ left: Math.max(0, Math.min(playheadX - 40, width - 80)) }}
        >
          {formatTime(currentTime)}
        </div>
      )}

      {/* Duration end marker */}
      {timeToX(duration) <= width && (
        <div
          className="absolute top-0 bottom-0 w-px bg-red-500/50 pointer-events-none"
          style={{ left: timeToX(duration) }}
        />
      )}
    </div>
  );
};
